const API_BASE = (
  process.env.REACT_APP_API_BASE || "https://api.getevo.app"
).replace(/\/$/, "");

export type FeatureRequestStatus =
  | "requested"
  | "in_progress"
  | "deployed";

export type FeatureRequest = {
  id: string;
  title: string;
  description: string;
  status: FeatureRequestStatus;
  author_name: string;
  vote_count: number;
  viewer_has_voted: boolean;
  can_edit: boolean;
  created_at: string;
  updated_at: string;
};

export type FeatureRequestList = {
  requests: FeatureRequest[];
  is_admin: boolean;
};

type FeatureRequestInput = {
  title: string;
  description: string;
};

async function request(
  accessToken: string,
  path: string,
  init: RequestInit = {},
) {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const fallback = "Something went wrong. Please try again.";
    const body = (await response.text()).trim();
    if (!body) throw new Error(fallback);

    let message = body;
    try {
      const error = JSON.parse(body) as { message?: unknown };
      if (typeof error.message === "string" && error.message.trim()) {
        message = error.message.trim();
      }
    } catch {
      // Some upstream errors are plain text rather than JSON.
    }
    throw new Error(message);
  }

  return response;
}

export async function listFeatureRequests(
  accessToken: string,
  status: FeatureRequestStatus | "all" = "all",
): Promise<FeatureRequestList> {
  const response = await request(
    accessToken,
    `/api/v1/feature_requests?status=${encodeURIComponent(status)}`,
  );
  return response.json();
}

export async function createFeatureRequest(
  accessToken: string,
  input: FeatureRequestInput,
) {
  await request(accessToken, "/api/v1/feature_requests", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateFeatureRequest(
  accessToken: string,
  requestID: string,
  input: FeatureRequestInput,
) {
  await request(
    accessToken,
    `/api/v1/feature_requests/${encodeURIComponent(requestID)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export async function updateFeatureRequestStatus(
  accessToken: string,
  requestID: string,
  status: FeatureRequestStatus,
) {
  await request(
    accessToken,
    `/api/v1/feature_requests/${encodeURIComponent(requestID)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ status }),
    },
  );
}

export async function deleteFeatureRequest(
  accessToken: string,
  requestID: string,
) {
  await request(
    accessToken,
    `/api/v1/feature_requests/${encodeURIComponent(requestID)}`,
    { method: "DELETE" },
  );
}

export async function addFeatureRequestVote(
  accessToken: string,
  requestID: string,
) {
  await request(
    accessToken,
    `/api/v1/feature_requests/${encodeURIComponent(requestID)}/vote`,
    { method: "PUT" },
  );
}

export async function removeFeatureRequestVote(
  accessToken: string,
  requestID: string,
) {
  await request(
    accessToken,
    `/api/v1/feature_requests/${encodeURIComponent(requestID)}/vote`,
    { method: "DELETE" },
  );
}
